import { ChangeEvent, FC, useEffect, useState } from 'react';
import { Col, Popover, Radio, RadioChangeEvent, Row, Checkbox, Collapse, CollapseProps } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import Input from '../../../../components/input/Input';
import Dropdown from '../../../../components/dropdown/Dropdown';
import WarningIcon from '../../../../assets/icons/WarningIcon';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
    removeOnboardingPersonalInfo,
    setIsChangeNationality,
    updateOnboardingPersonalInfo,
} from '../../../../redux/reducers/onboarding/sliceReducer';
import { countries } from '../../../../common/constants/mockCountries';
import { createMask } from '../../../../common/utils/inputMaskUtils';
import UploadDropzone from '../../../../components/upload-dropzone/UploadDropzone';
import { Roles } from '../../../../common/enums/Roles';
import CommonDatePicker from '../../../../components/date-picker/CommonDatePicker';
import { uploadFileOnboardingPersonalInfo } from '../../../../redux/reducers/onboarding/tabs/personalInformationAsyncThunk';
import HistoryComponent from '../../history/HistoryComponent';
import { FieldKeys } from '../../../../common/api/services/HistoryApi/types/fieldKeys';
import { historyApi } from '../../../../common/api/services/HistoryApi/historyApi';
import { useParams } from 'react-router-dom';
import { OnboardingFileKeys } from '../../../../common/api/services/HistoryApi/common/OnboardingFileKeys';
import { ROUTER_PATHS } from '../../../../navigation/routerPaths';
import { setActiveFields, setActiveKeys } from '../../../../redux/reducers/onboarding-history/sliceReducer';
import { ShownTypes } from '../../../../redux/reducers/onboarding-history/types';
import { initialHistoryFields } from '../../constants/initialHistoryFields';
import { CaretRightOutlined } from '@ant-design/icons';

const genders = ['man', 'woman'];
const languages = ['fr', 'en', 'de', 'it'];
const maritalStatuses = ['Single', 'Married', 'Widow(er)', 'Separated', 'Divorced'];

const PersonalInfoTab: FC = () => {
    const authState = useAppSelector((state) => state.auth);

    const { consultantId } = useParams<{
        consultantId: string;
    }>();

    const { role } = useAppSelector((state) => state.auth);
    const isAdmin = role === Roles.ADMIN || role === Roles.MANAGER;
    const {
        lastName,
        firstName,
        phone,
        email,
        language,
        dateOfBirth,
        nationality,
        gender,
        maritalStatus,
        workForm,
        passportExpirationDate,
        hasSwissAddress,
        hasAbroadAddress,
        maritalStatusDetailsSpouseSSN,
        hasCBA,
        hasOtherJob,
        documents,
        hasTreasury,
        hasAdditionalAccidentInsurance,
    } = useAppSelector((state) => state.onboarding.personalInfo);
    const errorMessages = useAppSelector((state) => state.onboarding.validationError.personalInfo);
    const { activeKeys } = useAppSelector((state) => state.onBoardingHistory);

    useEffect(() => {
        const matchedKey = OnboardingFileKeys.find((item) =>
            Object.keys(item).some((key) => key === ROUTER_PATHS.personalInformation)
        );
        if (matchedKey) {
            historyApi
                .getOnboardingActiveFields({
                    userId: consultantId || 'consultant',
                    keys: matchedKey[ROUTER_PATHS.personalInformation],
                })
                .then((resp) => dispatch(setActiveFields(resp.data)));
        }
    }, []);

    const isConsultant = useAppSelector((state) => state.auth.role === Roles.CONSULTANT);
    const dispatch = useAppDispatch();

    const changeDate = (fieldName: string) => (date: Date | null) => {
        const value = date?.toDateString() ?? '';
        dispatch(updateOnboardingPersonalInfo({ [fieldName]: value }));
    };

    const onChangeDropdown = (fieldName: string) => (data: any) => {
        if (fieldName === 'nationality') {
            dispatch(setIsChangeNationality(true));
        }
        dispatch(updateOnboardingPersonalInfo({ [fieldName]: data }));
    };

    const onChange = (event: ChangeEvent<HTMLInputElement> | RadioChangeEvent) => {
        const fieldName = event.target.name!;
        const fieldValue = event.target.value;

        if (event.target.name === 'email') {
            dispatch(updateOnboardingPersonalInfo({ email: fieldValue.trim() }));
            return;
        }

        if (fieldName === 'maritalStatusDetailsIsSpouseSwissResident' && !fieldValue) {
            dispatch(updateOnboardingPersonalInfo({ maritalStatusDetailsSpouseSSN: '' }));
        }

        if (fieldName === 'maritalStatusDetailsSpousalIncome' && !fieldValue) {
            dispatch(
                updateOnboardingPersonalInfo({
                    maritalStatusDetailsSpouseWorkCountry: '',
                    maritalStatusDetailsSpouseWorkCanton: '',
                    maritalStatusDetailsSpouseWorkCity: '',
                    maritalStatusDetailsSpouseStartedWorkingFrom: '',
                })
            );
        }

        if (fieldName === 'hasSwissAddress' && !fieldValue) {
            dispatch(
                updateOnboardingPersonalInfo({
                    swissAddressStreet: '',
                    swissAddressPostCode: '',
                    swissAddressCity: '',
                })
            );
        }

        if (fieldName === 'hasAbroadAddress' && !fieldValue) {
            dispatch(
                updateOnboardingPersonalInfo({
                    abroadAddressStreet: '',
                    abroadAddressPostCode: '',
                    abroadAddressCity: '',
                    abroadAddressCountry: '',
                    abroadAddressTotalAttendance: '',
                })
            );
            dispatch(
                updateOnboardingPersonalInfo({
                    abroadAddressStreet: '',
                    abroadAddressPostCode: '',
                    abroadAddressCity: '',
                    abroadAddressCountry: '',
                    abroadAddressTotalAttendance: '',
                })
            );
        }

        if (fieldName === 'maritalStatusDetailsSpouseSSN') {
            dispatch(
                updateOnboardingPersonalInfo({
                    [fieldName]: createMask(fieldValue, maritalStatusDetailsSpouseSSN),
                })
            );
        } else {
            dispatch(updateOnboardingPersonalInfo({ [fieldName]: fieldValue }));
        }
    };

    const onChangePhone = (value: string) => {
        dispatch(updateOnboardingPersonalInfo({ phone: value }));
    };

    const onChangeChecked = (event: CheckboxChangeEvent) => {
        const fieldName = event.target.name!;
        const fieldValue = event.target.checked;

        dispatch(updateOnboardingPersonalInfo({ [fieldName]: fieldValue }));
    };

    const getError = (fieldName: string): string => {
        if (errorMessages) {
            return errorMessages[fieldName] ?? '';
        } else {
            return '';
        }
    };

    const isMarried = maritalStatus === 'Married';
    const isAddressError = !!getError('hasAbroadAddress');

    const toggleAll = () => {
        if (activeKeys.length === collapseItems.length) {
            dispatch(setActiveKeys([]));
        } else {
            dispatch(setActiveKeys(collapseItems.map((item: any) => item.key)));
        }
    };

    const collapseItems: any = [
        {
            key: '1',
            label: 'Personal details',
            children: (
                <div style={{ marginTop: '10px' }}>
                    <h4>General</h4>
                    <Row gutter={[16, 16]}>
                        <Col span={6}>
                            <Input
                                name="firstName"
                                label="First name"
                                value={firstName}
                                onChange={onChange}
                                errorMessage={getError('data.firstName')}
                            />
                        </Col>
                        <Col span={6}>
                            <Input
                                name="lastName"
                                label="Last name"
                                value={lastName}
                                onChange={onChange}
                                errorMessage={getError('data.lastName')}
                            />
                        </Col>
                        <Col span={6}>
                            <Input
                                name="phone"
                                label="Phone number"
                                value={phone}
                                onChange={onChangePhone}
                                errorMessage={getError('data.phone')}
                                disabled={isConsultant}
                                inputType="phone"
                            />
                        </Col>
                        <Col span={6}>
                            <Input
                                name="email"
                                label="Email"
                                value={email}
                                onChange={onChange}
                                errorMessage={getError('data.email')}
                                disabled={isConsultant}
                            />
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        <Col span={6}>
                            <CommonDatePicker
                                label="Date of birth"
                                defaultValue={dateOfBirth}
                                changeDate={changeDate('dateOfBirth')}
                                className="date-picker-form"
                                errorMessage={getError('data.dateOfBirth')}
                                calendarPosition="right-calendar-position"
                            />
                        </Col>
                        <Col span={6}>
                            <Dropdown
                                options={genders}
                                value={gender}
                                onChange={onChangeDropdown('gender')}
                                label="Gender"
                                showSearch={false}
                                errorMessage={getError('data.gender')}
                                disabled={false}
                                allowClear
                                onClear={() => onChangeDropdown('gender')('')}
                            />
                        </Col>
                        <Col span={6}>
                            <Dropdown
                                options={languages}
                                value={language}
                                onChange={onChangeDropdown('language')}
                                label="Language"
                                showSearch={false}
                                errorMessage={getError('data.language')}
                                allowClear
                                onClear={() => onChangeDropdown('language')('')}
                            />
                        </Col>
                        <Col span={6}>
                            <Dropdown
                                options={countries}
                                allowClear
                                value={nationality || undefined}
                                onChange={onChangeDropdown('nationality')}
                                label="Nationality"
                                customClassName="dropdown-placeholder"
                                placeholder="Type to search"
                                errorMessage={getError('data.nationality')}
                                isSearch={true}
                            />
                        </Col>
                    </Row>
                    {nationality === 'Switzerland' && (
                        <Row justify="start" align="middle" gutter={[16, 16]} style={{ height: 54 }}>
                            <Col span={6}>
                                <span>Passport expiration date</span>
                            </Col>
                            <Col span={6} style={{ height: '54px' }}>
                                <CommonDatePicker
                                    label="End date"
                                    defaultValue={passportExpirationDate}
                                    changeDate={changeDate('passportExpirationDate')}
                                    className="date-picker-form"
                                    errorMessage={getError('data.passportExpirationDate')}
                                    calendarPosition="right-calendar-position"
                                />
                            </Col>
                        </Row>
                    )}
                    <HistoryComponent
                        fieldKey={FieldKeys.maritalStatus}
                        tabName="Marital status"
                        onChangeArray={[]}
                        shownElements={initialHistoryFields[FieldKeys.maritalStatus]}
                    />
                    <div className="document-title" style={{ marginTop: '1rem' }}>
                        <h4>Documents (ID/Passport)</h4>
                        <Popover
                            className="info-pop-button"
                            placement="rightTop"
                            content={<DocumentInfContent />}
                            trigger="hover"
                            overlayClassName="info-pop-content"
                            showArrow={false}
                        >
                            <button>
                                <WarningIcon />
                            </button>
                        </Popover>
                    </div>
                    <div style={{ marginTop: '1rem' }}>
                        <UploadDropzone
                            defaultFiles={documents}
                            onSubmit={(files) =>
                                dispatch(
                                    uploadFileOnboardingPersonalInfo({ files, ownerId: consultantId || authState._id })
                                )
                            }
                            removeFile={(file) => dispatch(removeOnboardingPersonalInfo(file))}
                            errorMessage={getError('data.documents')}
                            isDownload
                        />
                    </div>
                    <Row justify="start" style={{ padding: '0 5px' }}>
                        <Col span={12}>
                            <Row
                                justify="space-between"
                                align="middle"
                                gutter={[16, 16]}
                                style={{
                                    height: 54,
                                    borderRadius: '12px',
                                    padding: '0 15px',
                                    width: '98%',
                                    backgroundColor: '#f5f7fa',
                                }}
                            >
                                <Col>
                                    <span>How do you work?</span>
                                </Col>
                                <Col>
                                    <Radio.Group name="workForm" value={workForm} onChange={onChange}>
                                        <Radio value="corporation">As corporation</Radio>
                                        <Radio value="payroll">As payroll</Radio>
                                    </Radio.Group>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </div>
            ),
        },
        {
            key: '2',
            label: 'Address',
            children: (
                <>
                    <div className="address-title">
                        <h4>Address</h4>
                        <Popover
                            className="info-pop-button"
                            placement="topRight"
                            content={<AddressInfoContent />}
                            trigger="hover"
                            overlayClassName="info-pop-content"
                            showArrow={false}
                        >
                            <button>
                                <WarningIcon />
                            </button>
                        </Popover>
                    </div>

                    <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ height: 54 }}>
                        <Col>
                            <span style={{ color: isAddressError ? '#c43025' : '#10132f' }}>
                                Do you have a Swiss address?
                            </span>
                        </Col>
                        <Col>
                            <Radio.Group name="hasSwissAddress" value={hasSwissAddress} onChange={onChange}>
                                <Radio value={true}>Yes</Radio>
                                <Radio value={false}>No</Radio>
                            </Radio.Group>
                        </Col>
                    </Row>
                    {hasSwissAddress && (
                        <HistoryComponent
                            fieldKey={FieldKeys.swissAddress}
                            tabName="Swiss address"
                            onChangeArray={[]}
                            shownElements={initialHistoryFields[FieldKeys.swissAddress]}
                        />
                    )}

                    {/* {hasSwissAddress && (
          <>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Input
                  name="swissAddressStreet"
                  label="Street name"
                  value={swissAddressStreet}
                  onChange={onChange}
                  errorMessage={getError('data.swissAddress.streetName')}
                />
              </Col>
              <Col span={12}>
                <Input
                  name="swissAddressNumber"
                  label="Street number"
                  value={swissAddressNumber?.toString()}
                  onChange={onChange}
                  errorMessage={getError('data.swissAddress.streetNumber')}
                />
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Input
                  name="swissAddressState"
                  label="State"
                  value={swissAddressState}
                  onChange={onChange}
                  errorMessage={getError('data.swissAddress.state')}
                />
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Input
                  name="swissAddressPostCode"
                  label="Postcode"
                  value={swissAddressPostCode}
                  onChange={onChange}
                  errorMessage={getError('data.swissAddress.postCode')}
                />
              </Col>
              <Col span={12}>
                <Input
                  name="swissAddressCity"
                  label="City"
                  value={swissAddressCity}
                  onChange={onChange}
                  errorMessage={getError('data.swissAddress.city')}
                />
              </Col>
            </Row>
          </>
        )} */}

                    <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ height: 54 }}>
                        <Col>
                            <span style={{ color: isAddressError ? '#c43025' : '#10132f' }}>
                                Do you have an address abroad?
                            </span>
                        </Col>
                        <Col>
                            <Radio.Group value={hasAbroadAddress} name="hasAbroadAddress" onChange={onChange}>
                                <Radio value={true}>Yes</Radio>
                                <Radio value={false}>No</Radio>
                            </Radio.Group>
                        </Col>
                    </Row>
                    {hasAbroadAddress && (
                        <HistoryComponent
                            fieldKey={FieldKeys.abroadAddress}
                            tabName="Abroad address"
                            onChangeArray={[]}
                            shownElements={initialHistoryFields[FieldKeys.abroadAddress]}
                        />
                    )}

                    {isAddressError && (
                        <Row justify="space-between" align="middle" gutter={[16, 16]}>
                            <Col span={24}>
                                <span className="personal-info-address-error">One of the addresses must be YES</span>
                            </Col>
                        </Row>
                    )}
                </>
            ),
        },
        {
            key: '3',
            label: 'Expenses',
            children: (
                <>
                    {isAdmin && (
                        <HistoryComponent
                            fieldKey={FieldKeys.expensesForOptimization}
                            tabName="Expenses for optimization"
                            onChangeArray={[]}
                            shownElements={initialHistoryFields[FieldKeys.expensesForOptimization]}
                        />
                    )}
                    {isAdmin && (
                        <HistoryComponent
                            fieldKey={FieldKeys.mealAllowance}
                            tabName="Meal Allowance"
                            onChangeArray={[]}
                            shownElements={initialHistoryFields[FieldKeys.mealAllowance]}
                        />
                    )}
                    {isAdmin && (
                        <HistoryComponent
                            fieldKey={FieldKeys.representativeAllowance}
                            tabName="Representative allowance"
                            onChangeArray={[]}
                            shownElements={initialHistoryFields[FieldKeys.representativeAllowance]}
                        />
                    )}
                </>
            ),
        },
        {
            key: '4',
            label: 'Contract Information',
            children: (
                <>
                    <Row justify="start" style={{ padding: '0 5px' }}>
                        <Col span={12}>
                            <Row
                                justify="space-between"
                                align="middle"
                                gutter={[16, 16]}
                                style={{
                                    height: 54,
                                    backgroundColor: '#f5f7fa',
                                    borderRadius: '12px',
                                    padding: '0 15px',
                                    width: '98%',
                                }}
                            >
                                <Col span={17}>
                                    <span>Complementary accident insurance:</span>
                                    <Popover
                                        className="info-pop-button"
                                        placement="rightTop"
                                        content={
                                            <p>
                                                Standard coverage is 80% of your salary up to CHF 148&apos;200.
                                                <br /> Complementary insurance covers 80% of your entire salary.
                                            </p>
                                        }
                                        trigger="hover"
                                        overlayClassName="info-pop-content"
                                        showArrow={false}
                                    >
                                        <button>
                                            <WarningIcon />
                                        </button>
                                    </Popover>
                                </Col>
                                <Col span={7}>
                                    <Radio.Group
                                        name="hasAdditionalAccidentInsurance"
                                        value={hasAdditionalAccidentInsurance}
                                        onChange={onChange}
                                    >
                                        <Radio value={true}>Yes</Radio>
                                        <Radio value={false}>No</Radio>
                                    </Radio.Group>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    {isAdmin && (
                        <HistoryComponent
                            fieldKey={FieldKeys.accidentRate}
                            onChangeArray={[]}
                            shownElements={initialHistoryFields[FieldKeys.accidentRate]}
                            tabName="Accident Rate"
                        />
                    )}
                    {!isConsultant && (
                        <HistoryComponent
                            fieldKey={FieldKeys.lossOfIncome}
                            onChangeArray={[]}
                            shownElements={initialHistoryFields[FieldKeys.lossOfIncome]}
                            tabName="Loss of income"
                        />
                    )}
                    {!isConsultant && (
                        <Row justify="start" style={{ padding: '0 5px' }}>
                            <Col span={12}>
                                <Row
                                    justify="space-between"
                                    align="middle"
                                    gutter={[16, 16]}
                                    style={{
                                        height: 54,
                                        backgroundColor: '#f5f7fa',
                                        borderRadius: '12px',
                                        padding: '0 15px',
                                        width: '98%',
                                    }}
                                >
                                    <Col>
                                        <span>Are you CBA/CCT?</span>
                                        <span className="asterisk">*</span>
                                    </Col>
                                    <Col>
                                        <Radio.Group name="hasCBA" value={hasCBA} onChange={onChange}>
                                            <Radio value={true}>Yes</Radio>
                                            <Radio value={false}>No</Radio>
                                        </Radio.Group>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    )}
                    {hasCBA && !isConsultant && (
                        <HistoryComponent
                            fieldKey={FieldKeys.cba}
                            onChangeArray={[]}
                            shownElements={initialHistoryFields[FieldKeys.cba]}
                            tabName="CBA/CCT"
                        />
                    )}
                    {!isConsultant && (
                        <Row justify="start" style={{ padding: '0 5px' }}>
                            <Col span={12}>
                                <Row
                                    justify="space-between"
                                    align="middle"
                                    gutter={[16, 16]}
                                    style={{
                                        height: 54,
                                        backgroundColor: '#f5f7fa',
                                        borderRadius: '12px',
                                        padding: '0 15px',
                                        width: '98%',
                                    }}
                                >
                                    <Col>
                                        <span>Do you have a treasury?</span>
                                        <span className="asterisk">*</span>
                                    </Col>
                                    <Col>
                                        <Radio.Group
                                            name="hasTreasury"
                                            value={!!hasTreasury}
                                            disabled={isConsultant}
                                            onChange={onChange}
                                        >
                                            <Radio value={true}>Yes</Radio>
                                            <Radio value={false}>No</Radio>
                                        </Radio.Group>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    )}
                    {hasTreasury && isAdmin && (
                        <HistoryComponent
                            fieldKey={FieldKeys.treasury}
                            onChangeArray={[]}
                            shownElements={initialHistoryFields[FieldKeys.treasury]}
                            tabName="Treasury"
                        />
                    )}
                    {isAdmin && (
                        <HistoryComponent
                            fieldKey={FieldKeys.paymentCurrency}
                            tabName="Currency"
                            onChangeArray={[]}
                            shownElements={initialHistoryFields[FieldKeys.paymentCurrency]}
                        />
                    )}
                </>
            ),
        },
        {
            key: '5',
            label: 'Working time',
            children: (
                <>
                    <HistoryComponent
                        fieldKey={FieldKeys.workTime}
                        onChangeArray={[]}
                        shownElements={initialHistoryFields[FieldKeys.workTime]}
                        tabName="Working Time for Numeriq"
                    />
                    <Row justify="start" style={{ padding: '0 5px' }}>
                        <Col span={12}>
                            <Row
                                justify="space-between"
                                align="middle"
                                gutter={[16, 16]}
                                style={{
                                    height: 54,
                                    backgroundColor: '#f5f7fa',
                                    borderRadius: '12px',
                                    padding: '0 15px',
                                    width: '98%',
                                }}
                            >
                                <Col>Do you have other job (not in Numeriq)?</Col>
                                <Col>
                                    <Radio.Group name="hasOtherJob" value={!!hasOtherJob} onChange={onChange}>
                                        <Radio value={true}>Yes</Radio>
                                        <Radio value={false}>No</Radio>
                                    </Radio.Group>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    {hasOtherJob && (
                        <HistoryComponent
                            fieldKey={FieldKeys.otherJob}
                            onChangeArray={[]}
                            shownElements={initialHistoryFields[FieldKeys.otherJob]}
                            tabName="Working Time for other Job"
                        />
                    )}
                </>
            ),
        },
    ];

    const filteredCollapseItems = collapseItems.filter(
        (item: any) => !((item.key === '3' || item.key === '4' || item.key === '5') && !isAdmin)
    );

    return (
        <div className="onboarding-wrapper-v2">
            <Row justify="end">
                <span className="toggle-button-onboarding" onClick={toggleAll}>
                    {activeKeys.length === collapseItems.length ? 'Collapse All' : 'Expand All'}{' '}
                    <CaretRightOutlined rotate={activeKeys.length === collapseItems.length ? 270 : 90} />
                </span>
            </Row>
            <Collapse
                activeKey={activeKeys}
                onChange={(keys) => dispatch(setActiveKeys(keys as string[]))}
                style={{ width: '100%' }}
                className="onboarding-collapse"
                expandIconPosition="right"
                expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 270 : 90} />}
            >
                {filteredCollapseItems.map((item: any) => {
                    return (
                        <Collapse.Panel key={item.key} header={<span>{item.label}</span>}>
                            {item.children}
                        </Collapse.Panel>
                    );
                })}
            </Collapse>
        </div>
    );
};

export default PersonalInfoTab;

const DocumentInfContent: FC = () => <p>You will be asked to upload work permit in a later section</p>;

const AddressInfoContent: FC = () => (
    <p>If you change the address, you must inform Numeriq immediately as tax-at-source should be updated.</p>
);
